import React from 'react';
import './css/OpenStatus.css';

// Helper function to convert 12-hour time to 24-hour time
const convertTo24Hour = (time) => {
    const [hour, minute] = time.split(/[:\s]/).map(Number);
    const isPM = time.includes('PM');
    return `${(hour % 12 + (isPM ? 12 : 0)).toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}`;
};

const getStatusMessage = (openingHours) => {
    if (!openingHours || openingHours.length === 0) {
        return { status: 'Bez otváracích hodín', color: 'gray', sortStatus: 'no-hours' };
    }

    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const now = new Date();
    const today = daysOfWeek[now.getDay()];
    const currentTime = now.getHours() * 60 + now.getMinutes();

    // Find today's hours in openingHours
    const todayHours = openingHours.find((entry) => entry.startsWith(today));

    // If the place is closed today
    if (!todayHours || todayHours.includes('Closed')) {
        return { status: 'Dnes zatvorené', color: 'red', sortStatus: 'closed' };
    }

    // If invalid "Open 24 hours" data is found
    if (todayHours.includes('Open 24 hours')) {
        return { status: 'Bez otváracích hodín', color: 'gray', sortStatus: 'no-hours' };
    }

    // Parse multiple opening and closing time ranges
    const timeRanges = todayHours.match(/\d{1,2}:\d{2}\s[AP]M/g);
    if (!timeRanges || timeRanges.length % 2 !== 0) {
        return { status: 'Invalid hours', color: 'gray', sortStatus: 'no-hours' };
    }

    const ranges = [];
    for (let i = 0; i < timeRanges.length; i += 2) {
        const openingTime = timeRanges[i];
        const closingTime = timeRanges[i + 1];
        ranges.push({
            openingTime: convertTo24Hour(openingTime),
            closingTime: convertTo24Hour(closingTime),
            openingMinutes: (() => {
                const [hour, minute] = openingTime.split(/[:\s]/).map(Number);
                const isPM = openingTime.includes('PM');
                return (hour % 12 + (isPM ? 12 : 0)) * 60 + minute;
            })(),
            closingMinutes: (() => {
                const [hour, minute] = closingTime.split(/[:\s]/).map(Number);
                const isPM = closingTime.includes('PM');
                return (hour % 12 + (isPM ? 12 : 0)) * 60 + minute;
            })(),
        });
    }

    // Check if the current time falls within any valid range
    for (const range of ranges) {
        if (currentTime >= range.openingMinutes && currentTime <= range.closingMinutes) {
            return {
                status: `Otvorené do ${range.closingTime}`,
                color: 'green',
                sortStatus: 'open',
            };
        }
    }

    // If currently closed, show the next opening time
    const nextOpenRange = ranges.find((range) => currentTime < range.openingMinutes);
    if (nextOpenRange) {
        return {
            status: `Zatvorené, otvorené od ${nextOpenRange.openingTime}`,
            color: 'red',
            sortStatus: 'closed',
        };
    }

    // If no upcoming opening time is available
    return { status: 'Dnes zatvorené', color: 'red', sortStatus: 'closed' };
};

// Pure helper exported for other code (avoids calling the component as a function)
export const computeOpenStatus = (openingHours) => getStatusMessage(openingHours);

const OpenStatus = ({ openingHours, forSorting }) => {
    const { status, color, sortStatus } = getStatusMessage(openingHours);

    if (forSorting) return sortStatus;

    return <p className={`open-status ${color}`}>{status}</p>;
};

export default OpenStatus;
